import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import { Button, Typography, IconButton } from "stories/components";
import { Row, Col } from "stories/layout";
import { Input, DatePicker } from "stories/forms";
import { useNotification } from "stories/components/Notification";
import { useAppDialog } from "components/AppDialog/useAppDialog";
import FileDownloadButton from "components/Buttons/FileDownloadButton/FileDownloadButton";
import usePermissions from "hooks/usePermissions";

import {
  httpGetFundValuationReports,
  httpCreateFundValuationReport,
  httpUpdateFundValuationReport,
  httpDeleteFundValuationReport,
  httpUploadFundValuationReportAttachment,
  httpDeleteFundValuationReportAttachment,
} from "services/fund_valuation_reports";

import {
  FUND_VALUATION_ATTACHMENT_EXTENSIONS,
  FUND_VALUATION_DEFAULT_VALUES,
  buildFundValuationErrors,
  buildFundValuationSchema,
  fromApi,
  toPayload,
  trimDecimal,
  validateAttachment,
} from "./fundValuationPayload";

const COMMITMENT_FIELDS = ["fund_commitment", "left_commitment"];

const FundValuationReportEdit = ({
  fundId,
  fundName,
  report = null,
  portfolios = [],
  defaultPortfolio = "",
  handleResolve,
  handleReject,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { dialog, confirm } = useAppDialog();
  const { canWrite } = usePermissions();

  // Message that must survive later field validation (e.g. the row was saved but the
  // attachment upload failed).
  const [saveError, setSaveError] = useState(null);
  // The row exists on the server (created now or opened for edit): closing refreshes the list.
  const savedRef = useRef(false);
  // Commitment fields the user has typed into are never overwritten by the prefill.
  const typedRef = useRef({});
  const prefillRequestRef = useRef(0);
  const fileInputRef = useRef(null);

  const formik = useFormik({
    initialValues: report
      ? fromApi(report)
      : { ...FUND_VALUATION_DEFAULT_VALUES, portfolio: defaultPortfolio || "" },
    validationSchema: buildFundValuationSchema(t("fund_valuation_report_required", "Kenttä on pakollinen"), t),
    validate: (values) => {
      const error = validateAttachment(values.attachment, t);
      return error ? { attachment: error } : {};
    },
    validateOnMount: false,
    onSubmit: async (values, { setErrors, setFieldValue, setSubmitting }) => {
      setSaveError(null);
      const body = toPayload(values, fundId);
      let id = values.id;
      try {
        const res = id
          ? await httpUpdateFundValuationReport(id, body)
          : await httpCreateFundValuationReport(body);
        id = res?.data?.id ?? id;
      } catch (error) {
        setSubmitting(false);
        // 403 is shown by the global API error notifier.
        if (error?.status !== 403) {
          setErrors(buildFundValuationErrors(error?.data, t));
        }
        return;
      }
      savedRef.current = true;

      if (values.attachment) {
        try {
          await httpUploadFundValuationReportAttachment(id, values.attachment);
        } catch (error) {
          // The row is saved: stay open in edit mode so the upload can be retried.
          setFieldValue("id", id, false);
          setSubmitting(false);
          const fileError = error?.status === 400 ? buildFundValuationErrors(error?.data, t)?.attachment : null;
          if (fileError) {
            setErrors({ attachment: fileError });
          }
          setSaveError(
            t(
              "fund_valuation_report_attachment_upload_failed",
              "Raportti tallennettiin, mutta liitteen lataus epäonnistui"
            )
          );
          return;
        }
      }

      setSubmitting(false);
      notify({
        title: t("fund_valuation_report_title", "Käypä arvo"),
        message: values.id ? t("fund_valuation_report_updated", "Päivitetty") : t("fund_valuation_report_created", "Luotu"),
      });
      handleResolve && handleResolve();
    },
  });

  const { values, errors, touched, submitCount, setFieldValue, isSubmitting } = formik;

  useEffect(() => {
    if (report?.id) {
      savedRef.current = true;
    }
  }, [report]);

  const prefillCommitments = (portfolio) => {
    if (values.id || !portfolio || !fundId) {
      return;
    }
    const requestId = ++prefillRequestRef.current;
    httpGetFundValuationReports({ private_investment_fund: fundId, portfolio, limit: 1 })
      .then((res) => {
        if (requestId !== prefillRequestRef.current) {
          return;
        }
        const latest = res?.data?.results?.[0];
        COMMITMENT_FIELDS.forEach((field) => {
          if (typedRef.current[field]) {
            return;
          }
          formik.setFieldValue(field, latest ? trimDecimal(latest[field]) : "", false);
        });
      })
      .catch(() => {});
  };

  // A new row that opens with a portfolio already chosen prefills right away.
  useEffect(() => {
    if (!report && defaultPortfolio) {
      prefillCommitments(defaultPortfolio);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = (name) => (touched[name] || submitCount > 0) && errors[name] ? errors[name] : null;

  const inputProps = (name) => ({
    name,
    id: `fund_valuation_${name}`,
    value: values[name] ?? "",
    onChange: (e) => {
      if (COMMITMENT_FIELDS.includes(name)) {
        typedRef.current[name] = true;
      }
      formik.handleChange(e);
    },
    onBlur: formik.handleBlur,
    errorText: showError(name),
    disabled: !canWrite,
  });

  const decimalProps = (name) => ({
    ...inputProps(name),
    type: "text",
    inputMode: "decimal",
    autoComplete: "off",
  });

  const dateProps = (name) => ({
    name,
    id: `fund_valuation_${name}`,
    className: "form-control",
    selected: values[name],
    onChange: (date) => {
      setFieldValue(name, date);
      formik.setFieldTouched(name, true, false);
    },
    showYearDropdown: true,
    dateFormat: "d.M.yyyy",
    error: Boolean(showError(name)),
    errorText: showError(name),
    disabled: !canWrite,
  });

  const handlePortfolioChange = (e) => {
    const portfolio = e.target.value;
    setFieldValue("portfolio", portfolio);
    prefillCommitments(portfolio);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setFieldValue("attachment", file);
    formik.setFieldTouched("attachment", true, false);
  };

  const close = () => {
    if (savedRef.current) {
      handleResolve && handleResolve();
    } else {
      handleReject && handleReject();
    }
  };

  const handleDeleteAttachment = async () => {
    const ok = await confirm(t("fund_valuation_report_attachment_delete_confirm", "Poistetaanko liite?"));
    if (!ok) {
      return;
    }
    try {
      await httpDeleteFundValuationReportAttachment(values.id);
      setFieldValue("attachment_name", null, false);
      savedRef.current = true;
    } catch (error) {
      if (error?.status !== 403) {
        setSaveError(t("fund_valuation_report_attachment_delete_failed", "Liitteen poisto epäonnistui"));
      }
    }
  };

  const handleDelete = async () => {
    const ok = await confirm(t("fund_valuation_report_delete_confirm", "Poistetaanko raporttirivi liitteineen?"));
    if (!ok) {
      return;
    }
    try {
      await httpDeleteFundValuationReport(values.id);
      notify({ title: t("fund_valuation_report_title", "Käypä arvo"), message: t("fund_valuation_report_deleted", "Poistettu") });
      handleResolve && handleResolve();
    } catch (error) {
      if (error?.status !== 403) {
        setSaveError(t("fund_valuation_report_delete_failed", "Poisto epäonnistui"));
      }
    }
  };

  const title = values.id
    ? t("fund_valuation_report_edit_title", "Muokkaa raporttiriviä")
    : t("fund_valuation_report_new_title", "Uusi raportti");

  return (
    <div className="px-3 pt-2 fund-valuation-report-edit">
      {dialog}
      <Row className="mt-2 mb-2 border-bottom align-items-center">
        <Col xs={10} className="pb-3 pl-3">
          <Typography variant="h2" className="mb-0">{title}</Typography>
          {fundName ? <Typography variant="small" className="text-muted">{fundName}</Typography> : null}
        </Col>
        <Col xs={2} className="pb-3 pr-3 d-flex justify-content-end align-items-center">
          <IconButton aria-label={t("fund_valuation_report_close", "Sulje")} size="lg" onClick={close} iconName="times" data-dismiss="modal" />
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Input
            {...inputProps("portfolio")}
            type="select"
            label={t("fund_valuation_report_portfolio", "Salkku")}
            required
            onChange={handlePortfolioChange}
          >
            <option value="">{t("fund_valuation_report_choose_portfolio", "Valitse salkku")}</option>
            {(portfolios || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Input>
        </Col>
        <Col md={6} className="mb-3">
          <DatePicker
            {...dateProps("value_date")}
            label={t("fund_valuation_report_value_date", "Arvopäivä")}
            required
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Input {...decimalProps("market_value")} label={t("fund_valuation_report_market_value", "Käypä arvo")} />
        </Col>
        <Col md={6} className="mb-3">
          <Input
            {...inputProps("report_period")}
            type="text"
            maxLength={32}
            label={t("fund_valuation_report_report_period", "Raportointijakso")}
            placeholder={t("fund_valuation_report_report_period_placeholder", "esim. Q3/2026")}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Input {...decimalProps("owned_shares")} label={t("fund_valuation_report_owned_shares", "Kappalemäärä")} />
        </Col>
        <Col md={6} className="mb-3">
          <Input {...decimalProps("value_per_share")} label={t("fund_valuation_report_value_per_share", "Osuuden arvo")} />
        </Col>
      </Row>

      <Row>
        <Col md={4} className="mb-3">
          <Input {...decimalProps("fund_size")} label={t("fund_valuation_report_fund_size", "Rahaston koko")} />
        </Col>
        <Col md={4} className="mb-3">
          <Input {...decimalProps("fund_commitment")} label={t("fund_valuation_report_fund_commitment", "Sitoumus")} />
        </Col>
        <Col md={4} className="mb-3">
          <Input
            {...decimalProps("left_commitment")}
            label={t("fund_valuation_report_left_commitment", "Jäljellä oleva sitoumus")}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <DatePicker
            {...dateProps("report_received")}
            label={t("fund_valuation_report_report_received", "Raportti saapui")}
          />
        </Col>
        <Col md={6} className="mb-3">
          <DatePicker {...dateProps("ifrs_date")} label={t("fund_valuation_report_ifrs_date", "IFRS-päivä")} />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3">
          <Typography bold={false}>{t("fund_valuation_report_attachment", "Liite (rahaston raportti)")}</Typography>
          {values.id && values.attachment_name ? (
            <div className="d-flex align-items-center mb-2" data-testid="fund-valuation-current-attachment">
              <Typography className="mr-2 mb-0">{values.attachment_name}</Typography>
              <FileDownloadButton
                showPreview
                fileModel="FundValuationReport"
                fileId={values.id}
                fileName={values.attachment_name}
              />
              {canWrite ? (
                <Button size="sm" variant="secondary" className="ml-2" onClick={handleDeleteAttachment}>
                  {t("fund_valuation_report_attachment_delete", "Poista liite")}
                </Button>
              ) : null}
            </div>
          ) : null}
          {canWrite ? (
            <input
              ref={fileInputRef}
              type="file"
              name="attachment"
              data-testid="fund-valuation-attachment-input"
              className="form-control-file"
              accept={FUND_VALUATION_ATTACHMENT_EXTENSIONS.map((e) => `.${e}`).join(",")}
              onChange={handleFileChange}
            />
          ) : null}
          {values.id && values.attachment_name && canWrite ? (
            <Typography variant="small" className="text-muted">
              {t("fund_valuation_report_attachment_replace_hint", "Uusi tiedosto korvaa nykyisen liitteen.")}
            </Typography>
          ) : null}
          {showError("attachment") ? <Typography className="text-danger">{showError("attachment")}</Typography> : null}
        </Col>
      </Row>

      <Row className="bg-lighter pt-3 rounded-bottom">
        <Col className="mb-3 d-flex align-items-center flex-wrap gap-2">
          <Button onClick={close} variant="secondary">{t("fund_valuation_report_close", "Sulje")}</Button>
          {values.id && canWrite ? (
            <Button onClick={handleDelete} variant="danger">{t("fund_valuation_report_delete", "Poista")}</Button>
          ) : null}
        </Col>
        <Col className="mb-3 text-right">
          {canWrite ? (
            <Button variant="primary" disabled={isSubmitting} onClick={() => formik.submitForm()}>
              {t("fund_valuation_report_save", "Tallenna")}
            </Button>
          ) : null}
          {saveError ? <Typography className="text-danger">{saveError}</Typography> : null}
          {errors.general ? <Typography className="text-danger">{errors.general}</Typography> : null}
        </Col>
      </Row>
    </div>
  );
};

export default FundValuationReportEdit;
